import React from "react";
import { AutoComplete } from "@fidget/common/components/form/autoComplete/autoComplete";
import { intl } from "@fidget/common/i18n/TypedIntl";
import assigneeControlStyles from "../assigneeControl.css";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { useStore } from "../../../../models";
import { AppPermission } from "@closerplatform/spinner-openapi";

export enum AssigneeControlType {
  ToMe = "ToMe",
  ToAdviser = "ToAdviser",
  ToQueue = "ToQueue",
  ToGroupQueue = "ToGroupQueue"
}

interface Props {
  onChange: (value: AssigneeControlType | null) => void;
  currentType?: AssigneeControlType;
  showAssignToMe?: boolean;
  showAssignToTag?: boolean;
  showAssignToTagGroup?: boolean;
  isOpen?: boolean;
  initialIsOpen?: boolean;
  onInputClick?: () => void;
}

export const AssigneeControlTypePicker = ({
  onChange,
  currentType,
  showAssignToMe,
  showAssignToTag,
  showAssignToTagGroup,
  isOpen,
  initialIsOpen,
  onInputClick
}: Props) => {
  const { uiStore } = useStore();

  const filterByPermissions = (controlType: AssigneeControlType) => {
    const permissionCheck = (permission: AppPermission) => {
      return uiStore.canDisplayWithPermission(permission);
    };

    switch (controlType) {
      case AssigneeControlType.ToMe:
        return permissionCheck(AppPermission.RoomAssignSelf);
      case AssigneeControlType.ToAdviser:
        return permissionCheck(AppPermission.RoomAssignAgent);
      case AssigneeControlType.ToQueue:
        return permissionCheck(AppPermission.RoomAssignByTag);
      case AssigneeControlType.ToGroupQueue:
        return permissionCheck(AppPermission.RoomAssignByTagGroup);
      default:
        assertUnreachable(controlType);
        return true;
    }
  };

  const itemsProvider = () => {
    return Object.values(AssigneeControlType).filter(
      (v) =>
        (v !== AssigneeControlType.ToMe || !!showAssignToMe) &&
        (v !== AssigneeControlType.ToQueue || !!showAssignToTag) &&
        (v !== AssigneeControlType.ToGroupQueue || !!showAssignToTagGroup) &&
        filterByPermissions(v)
    );
  };

  return (
    <AutoComplete<AssigneeControlType>
      className={assigneeControlStyles["input-width"]}
      onChange={(value: AssigneeControlType | null | "") => (value ? onChange(value) : undefined)}
      itemsProvider={itemsProvider}
      itemToString={mapAssigneeCntrolTypeToString}
      inputValue={
        currentType
          ? mapAssigneeCntrolTypeToString(currentType)
          : intl.formatMessage("conversation.assignee.dropdown.empty")
      }
      initialIsOpen={initialIsOpen}
      isOpen={isOpen}
      readOnly
      onInputClick={onInputClick}
    />
  );
};

export const mapAssigneeCntrolTypeToString = (type: AssigneeControlType | null) => {
  if (type) {
    switch (type) {
      case AssigneeControlType.ToAdviser:
        return intl.formatMessage("conversation.assignee.dropdown.toPerson");
      case AssigneeControlType.ToQueue:
        return intl.formatMessage("conversation.assignee.dropdown.toQueue");
      case AssigneeControlType.ToMe:
        return intl.formatMessage("conversation.assignee.dropdown.toMe");
      case AssigneeControlType.ToGroupQueue:
        return intl.formatMessage("conversation.assignee.dropdown.toTagGroupQueue");
      default:
        assertUnreachable(type);
        return "";
    }
  }
  return "";
};
